import { EllipsisVertical, Key, Users, Zap } from "lucide-mui";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Fragment } from "react";
import {
  type Connection,
  type ConnectionHealth,
  type Provider,
  api,
  getErrorMessage,
} from "../../../../api/client.ts";
import { keys } from "../../../../api/queryKeys.ts";
import CrmProviderLogo from "../../../../components/CrmProviderLogo.tsx";
import DropdownMenu from "../../../../components/DropdownMenu.tsx";
import FormattedDate from "../../../../components/FormattedDate";
import LogoBadge from "../../../../components/LogoBadge.tsx";
import { humanize } from "../../../../utils/format.ts";
import StatusIcon, { StatusLabel } from "../../../../components/StatusIcon.tsx";

interface CrmConnectionListProps {
  connections: Connection[];
  providers: Provider[] | undefined;
  tenantsByConnection: Map<string, string[]>;
  tenantName: (tid: string) => string;
  onOpenMappings: (conn: Connection) => void;
  onOpenPeople: (conn: Connection) => void;
  onOpenCredentials: (conn: Connection) => void;
}

export default function CrmConnectionList({
  connections,
  providers,
  tenantsByConnection,
  tenantName,
  onOpenMappings,
  onOpenPeople,
  onOpenCredentials,
}: CrmConnectionListProps) {
  return (
    <Box>
      {connections.map((c, i) => (
        <Fragment key={c.id}>
          {i > 0 && <Divider />}
          <ConnectionRow
            connection={c}
            providerName={providers?.find((p) => p.id === c.provider)?.name ?? c.provider}
            tenantIds={tenantsByConnection.get(c.id) ?? []}
            tenantName={tenantName}
            onOpenMappings={() => onOpenMappings(c)}
            onOpenPeople={() => onOpenPeople(c)}
            onOpenCredentials={() => onOpenCredentials(c)}
          />
        </Fragment>
      ))}
    </Box>
  );
}

function ProviderLogoBadge({ provider }: { provider: string }) {
  return (
    <LogoBadge>
      <CrmProviderLogo provider={provider} />
    </LogoBadge>
  );
}

function ConnectionRow({
  connection: c,
  providerName,
  tenantIds,
  tenantName,
  onOpenMappings,
  onOpenPeople,
  onOpenCredentials,
}: {
  connection: Connection;
  providerName: string;
  tenantIds: string[];
  tenantName: (tid: string) => string;
  onOpenMappings: () => void;
  onOpenPeople: () => void;
  onOpenCredentials: () => void;
}) {
  const queryClient = useQueryClient();

  // Reports what this connection's token can really do with sequences.
  // Read-only on purpose: it lists sequences and resolves the sender, but never
  // enrols anyone, because a real enrolment would mail a live prospect.
  const probe = useMutation({
    mutationFn: () => api.testSequenceAccess(c.id),
    // A fresh probe may have just exercised a newly-granted scope; refresh the
    // connection list so a stale "missing scopes" chip clears without waiting
    // out the health TTL.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.crmConnection.all }),
  });

  const tokenHint = Object.values(c.credentials_hint).join(", ");

  return (
    <Box sx={{ py: 1.5 }}>
      <Stack direction="row" spacing={2} sx={{ alignItems: "flex-start" }}>
        <ProviderLogoBadge provider={c.provider} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 0.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
              {c.name || providerName}
            </Typography>
            <ConnectionStatusChip status={c.status} health={c.health} />
          </Stack>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {providerName}
            {tokenHint && (
              <>
                {" · Token "}
                <Box component="span" sx={{ fontFamily: "monospace" }}>
                  {tokenHint}
                </Box>
              </>
            )}
            {" · Added "}
            <FormattedDate value={c.created_at} />
          </Typography>
          {tenantIds.length > 0 && (
            <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: "wrap", rowGap: 0.5 }}>
              {tenantIds.map((tid) => (
                <Chip key={tid} size="small" variant="outlined" label={tenantName(tid)} />
              ))}
            </Stack>
          )}
        </Box>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", flexShrink: 0 }}>
          <Button size="small" onClick={onOpenMappings}>
            Tenants
          </Button>
          <DropdownMenu
            aria-label="More actions"
            icon={<EllipsisVertical fontSize="small" />}
            align="right"
          >
            <MenuItem onClick={() => onOpenPeople()}>
              <ListItemIcon>
                <Users fontSize="small" />
              </ListItemIcon>
              <ListItemText>People & clients</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => onOpenCredentials()}>
              <ListItemIcon>
                <Key fontSize="small" />
              </ListItemIcon>
              <ListItemText>Update credentials</ListItemText>
            </MenuItem>
            <MenuItem disabled={probe.isPending} onClick={() => probe.mutate()}>
              <ListItemIcon>
                <Zap fontSize="small" />
              </ListItemIcon>
              <ListItemText>Test sequence access</ListItemText>
            </MenuItem>
          </DropdownMenu>
        </Stack>
      </Stack>
      {probe.isPending && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          Testing sequence access…
        </Alert>
      )}
      {probe.isError && (
        <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => probe.reset()}>
          {getErrorMessage(probe.error)}
        </Alert>
      )}
      {probe.data && (
        <Alert
          severity={probe.data.status === "ok" ? "success" : "warning"}
          sx={{ mt: 1.5 }}
          onClose={() => probe.reset()}
        >
          {probe.data.status === "ok"
            ? `${probe.data.sequence_count} sequence(s) readable as user ${probe.data.user_id}.`
            : (probe.data.detail ?? probe.data.status)}
        </Alert>
      )}
    </Box>
  );
}

/**
 * Connection status plus scope health. A degraded connection has 403'd on a
 * scope within the tracking window (see backend crm/scope_health.py); the
 * missing scopes are listed so the operator knows exactly what to grant on the
 * private app.
 */
function ConnectionStatusChip({
  status,
  health,
}: {
  status: string;
  health: ConnectionHealth;
}) {
  if (health.status !== "degraded") {
    if (status === "active") return <StatusIcon kind="ok" label="Active" />;
    return <StatusLabel kind="attention" label={humanize(status)} />;
  }
  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Missing scopes — add these to the private app in the CRM and save:
          </Typography>
          {health.issues.map((i) => (
            <Typography
              key={i.scope}
              variant="caption"
              component="div"
              sx={{ fontFamily: "monospace" }}
            >
              {i.scope}
              {i.last_endpoint ? ` (${i.last_endpoint})` : ""}
            </Typography>
          ))}
        </Box>
      }
    >
      <span>
        <StatusLabel
          kind="error"
          label={`Missing ${health.missing_scopes.length} scope${
            health.missing_scopes.length === 1 ? "" : "s"
          }`}
        />
      </span>
    </Tooltip>
  );
}
